// A company's logo beside its name in the Doc Pabean table (revamp PR 7a). Same logo well, initials, country flag and
// logo/website report as the Mantine cell it replaces; the report popover is a Lumen Popover and its copy goes
// through t() in id/en/zh.
import { Flag, Globe } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/lumen/button';
import { Input } from '@/components/lumen/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/lumen/popover';
import { Separator } from '@/components/lumen/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { cn } from '@/lib/utils';
import { formatTemplate } from '@/lib/format-template';
import { useLanguage } from '@contexts/LanguageContext';
import { flagAssetUrl, toFlagCountryCode } from '@utils/country-flag';
import useReportCompanyLogo from '../../app/(dashboard)/doc-pabean/apis/use-cases/use-report-company-logo';

/**
 * Legal-form and honorific prefixes stripped before deriving initials. These
 * mirror the backend normalizer (domain/company_enrichment/models/normalize.go)
 * so a company reads the same on both sides: "PT Sarana Jaya" must become "SJ",
 * not "PS".
 */
const LEGAL_TOKENS = new Set(['pt', 'cv', 'firma', 'fa', 'tbk', 'persero', 'ptt', 'ms']);

/** Words that carry no identity, so they never contribute an initial. */
const GENERIC_TOKENS = new Set([
  'indonesia',
  'international',
  'manufacturing',
  'marketing',
  'asia',
  'pacific',
  'global',
  'limited',
  'private',
  'pte',
  'ltd',
  'co',
  'inc',
  'corp',
  'corporation',
  'company',
  'group',
  'industries',
  'industry',
]);

/**
 * companyInitials derives up to two letters from a company name.
 *
 * Legal forms and geography are dropped first: without that, half the shipper
 * column would read "PI" (PT ... Indonesia) and the badges would be useless as
 * a way to tell rows apart.
 */
export function companyInitials(name: string | null | undefined): string {
  if (!name) return '?';

  const tokens = name
    .toLowerCase()
    .replace(/[^a-z0-9\s&]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => !LEGAL_TOKENS.has(t));

  // Prefer distinctive words, but never return empty: a company literally
  // named "PT Indonesia" still needs a badge.
  const distinctive = tokens.filter((t) => !GENERIC_TOKENS.has(t));
  const source = distinctive.length > 0 ? distinctive : tokens;
  if (source.length === 0) return '?';

  if (source.length === 1) {
    return source[0].slice(0, 2).toUpperCase();
  }
  return (source[0][0] + source[1][0]).toUpperCase();
}

/** Slot index (1–6) for the company's well background. A separate index so the WELL palette can be static strings. */
function seriesSlot(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 6) + 1;
}

/** Static background classes for the monogram wells — Tailwind must see these at build time. */
const WELL = [
  'bg-series-1/15',
  'bg-series-2/15',
  'bg-series-3/15',
  'bg-series-4/15',
  'bg-series-5/15',
  'bg-series-6/15',
];

interface CompanyNameCellProps {
  name: string | null | undefined;
  logoUrl?: string | null;
  /**
   * True when the stored artwork is a site's "negative" variant — white, and
   * therefore invisible against a light row. Such logos render on a dark chip
   * instead of a transparent slot. The backend decides this from the pixels.
   */
  logoOnDark?: boolean | null;
  /** Resolved company website, shown so the user can check it. */
  website?: string | null;
  /**
   * Raw shipper/consignee country, in whatever shape the filing carried it —
   * an alpha-2 code, an alpha-3 code, or a full name. Passed only for the
   * shipper column, where the Country column was folded into this cell.
   */
  country?: string | null;
}

/** Strips the scheme so the popover shows a domain, not a URL. */
function displayDomain(website: string): string {
  return website
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
}

/**
 * The logo sits in a fixed-size well with object-fit: contain, so every row has an identically sized slot whatever
 * shape the logo is (square favicons sit centred, wide wordmarks letterbox). A load failure degrades to initials,
 * and a missing flag to the bare country code.
 */
export function CompanyNameCell({ name, logoUrl, logoOnDark, website, country }: CompanyNameCellProps) {
  const { t } = useLanguage();
  const [failed, setFailed] = useState(false);
  const [flagFailed, setFlagFailed] = useState(false);
  const [opened, setOpened] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const { report, loading } = useReportCompanyLogo();

  const label = name?.trim() || '-';
  const showLogo = Boolean(logoUrl) && !failed;
  // Only behind a logo that is actually showing: a dark chip behind initials would read as a different badge.
  const onDarkChip = showLogo && Boolean(logoOnDark);
  const iso2 = toFlagCountryCode(country);
  const flagSrc = flagAssetUrl(iso2);
  const hasCompany = label !== '-';
  const wellBg = WELL[seriesSlot(label) - 1];

  const send = async (kind: 'WRONG_LOGO' | 'SUGGEST_WEBSITE' | 'MISSING_LOGO') => {
    const ok = await report({
      companyName: label,
      kind,
      suggestedWebsite: kind === 'SUGGEST_WEBSITE' ? suggestion.trim() : undefined,
      observedLogoUrl: logoUrl ?? null,
      observedWebsite: website ?? null,
    });
    if (ok) {
      setOpened(false);
      setSuggestion('');
    }
  };

  // Brand logos made for dark backgrounds need a fixed dark plate in both modes; no fixed-dark Lumen token exists.
  const well = (
    <span
      className={cn(
        'flex h-[22px] w-14 shrink-0 items-center justify-center overflow-hidden rounded-tag text-fg',
        !showLogo && wellBg,
        onDarkChip && 'bg-[#1f2937] px-1 py-0.5'
      )}
    >
      {showLogo ? (
        <img
          src={logoUrl as string}
          alt="" // decorative: the company name is shown next to the logo
          onError={() => setFailed(true)}
          loading="lazy"
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="font-bold text-[10px] tracking-[0.3px]">{companyInitials(label)}</span>
      )}
    </span>
  );

  return (
    <div className="flex w-full items-center justify-start gap-2">
      {hasCompany ? (
        <Popover open={opened} onOpenChange={setOpened}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={formatTemplate(t('lumen.docPabean.logoOf'), { name: label })}
              className="flex cursor-pointer rounded-tag outline-none focus-visible:ring-[3px] focus-visible:ring-label/40"
            >
              {well}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[280px] p-3">
            <div className="grid gap-2">
              <p className="font-semibold text-fg text-xs">{label}</p>
              {/* The domain, not a confidence score: seeing the wrong company's site is what makes a wrong logo
                  obvious. */}
              <p className="flex items-center gap-1.5 break-all text-fg-muted text-xs">
                <Globe aria-hidden className="size-3.5 shrink-0" />
                {website ? displayDomain(website) : t('lumen.docPabean.logoNoWebsite')}
              </p>
              <Separator />
              {showLogo ? (
                <Button
                  size="xs"
                  variant="ghost"
                  className="bg-err/10 text-err-ink hover:bg-err/15 hover:text-err-ink"
                  disabled={loading}
                  onClick={() => send('WRONG_LOGO')}
                >
                  <Flag aria-hidden />
                  {t('lumen.docPabean.logoWrong')}
                </Button>
              ) : (
                <Button size="xs" variant="secondary" disabled={loading} onClick={() => send('MISSING_LOGO')}>
                  {t('lumen.docPabean.logoMissing')}
                </Button>
              )}
              {/* The website is the expensive half of enrichment: someone who knows their own trading partner
                  resolves in one step what search and guessing could not. */}
              <Input
                className="h-8 text-xs"
                placeholder={t('lumen.docPabean.logoWebsitePlaceholder')}
                value={suggestion}
                onChange={(e) => setSuggestion(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && suggestion.trim()) send('SUGGEST_WEBSITE');
                }}
              />
              <Button size="xs" disabled={!suggestion.trim() || loading} onClick={() => send('SUGGEST_WEBSITE')}>
                {t('lumen.docPabean.logoSendWebsite')}
              </Button>
              <p className="text-[10px] text-fg-muted">{t('lumen.docPabean.logoReviewNote')}</p>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        well
      )}

      <span className="min-w-0 flex-1 whitespace-normal break-words text-fg text-xs leading-[1.3] [overflow-wrap:anywhere]">
        {label}
      </span>

      {/* Country of the shipper, folded in from the removed Country column: the flag carries it at a glance, the code
          keeps it exact. It never shrinks, so the name absorbs any squeeze by wrapping. */}
      {iso2 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex shrink-0 items-center gap-1">
              {flagSrc && !flagFailed && (
                <img
                  src={flagSrc}
                  alt="" // decorative: the country code is shown next to the flag
                  onError={() => setFlagFailed(true)}
                  loading="lazy"
                  className="block h-3 w-4 rounded-[2px] object-cover shadow-[inset_0_0_0_1px_var(--lm-line)]"
                />
              )}
              <span className="font-mono font-semibold text-[10px] text-fg-muted tracking-[0.3px]">{iso2}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>{country?.trim() || iso2}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
